import { useRandomId, ConditionalWrapper, mergeRefs, useWindowDimensions, useOnClickOutside, useOnEscape, useOnMount, warnAboutMissingStyles } from '@entur/utils';
import React, { useRef, useState, useEffect } from 'react';
import { useDateFieldState, useDatePickerState, useTimeFieldState } from '@react-stately/datepicker';
import { useDateSegment, useDateField, useDatePicker, useTimeField } from '@react-aria/datepicker';
import { useLocale, I18nProvider } from '@react-aria/i18n';
import classNames from 'classnames';
import { BaseFormControl, useVariant, useInputGroupContext, isFilled, TextField } from '@entur/form';
import { CalendarDate, Time, getLocalTimeZone, GregorianCalendar, toCalendarDateTime, toTime, now, toZoned, today, toCalendarDate, startOfWeek, startOfYear, ZonedDateTime, parseAbsolute, CalendarDateTime, isEqualDay, getWeeksInMonth, parseTime } from '@internationalized/date';
export { CalendarDate, CalendarDateTime, Time, ZonedDateTime } from '@internationalized/date';
import { useCalendarCell, useCalendarGrid, useCalendar } from '@react-aria/calendar';
import { useCalendarState } from '@react-stately/calendar';
import { LeftArrowIcon, RightArrowIcon, CalendarIcon, DateIcon, ClockIcon } from '@entur/icons';
import { useButton } from '@react-aria/button';
import { IconButton } from '@entur/button';
import { VisuallyHidden } from '@entur/a11y';
import { useFloating, autoUpdate, offset, flip, shift } from '@floating-ui/react-dom';
import FocusLock from 'react-focus-lock';
import { space, zIndexes } from '@entur/tokens';
import { Modal } from '@entur/modal';

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (e.includes(n)) continue;
    t[n] = r[n];
  }
  return t;
}

var _excluded$b = ["segment", "state"];
var FieldSegment = function FieldSegment(_ref) {
  var segment = _ref.segment,
    state = _ref.state,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$b);
  var ref = useRef(null);
  var _useDateSegment = useDateSegment(segment, state, ref),
    segmentProps = _useDateSegment.segmentProps;
  return React.createElement("div", _extends({}, segmentProps, {
    ref: ref,
    className: classNames('eds-date-and-time-field__segment', {
      'eds-date-and-time-field__segment--placeholder': segment.isPlaceholder,
      'eds-date-and-time-field__segment--dot-separator': segment.text === '.' || segment.text === ':'
    }),
    tabIndex: state.isDisabled ? -1 : segmentProps.tabIndex
  }, rest), segment.text);
};

var nativeDateToDateTime = function nativeDateToDateTime(date, timeZone, offset) {
  if (timeZone) {
    if (offset) {
      return new ZonedDateTime(date.getFullYear(), date.getMonth() + 1, date.getDate(), timeZone, offset, date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
    }
    return parseAbsolute(date.toISOString(), timeZone);
  }
  return new CalendarDateTime(date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
};
/**
 * Tar inn et JS Date-objekt og returnerer et av DateValue-objektene fra @internationalized/date-pakken
 * @param {Date | null} date JS Date-objekt som ønskes konvertert til et DateValue-objekt
 * @param {boolean} noTimeOnlyDate Hvis tidspunktet er irrelevant kan denne settes til true, da får man et CalendarDate-objekt uten tidspunkt tilbake
 * @param {string} timeZone Tidssonen på IANA-formatet som tidpunktet skal konverteres til. Utelates denne får man et tidspunkt uten tidssone. Kan brukes med og uten en UTC-offset Vær obs på annen oppførsel med offset, les mer på beskrivelsen av offset
 * @param {number} offset UTC-offset i millisekunder, må brukes med en tidssone. Ved å legge på en offset lager du en variant av en tidssone. Det betyr at tidspunktet ikke endres (time, minutt, sekund uendret), men tidssonen, med tilhørende offset, tidspunktet er i endres.
 * @returns {CalendarDateTime | ZonedDateTime | CalendarDate | null} et av DateValue-objektene med verdier fra date eller null
 */
function nativeDateToDateValue(date, noTimeOnlyDate, timeZone, offset) {
  if (noTimeOnlyDate === void 0) {
    noTimeOnlyDate = false;
  }
  if (date === null) return null;
  if (noTimeOnlyDate) return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  return nativeDateToDateTime(date, timeZone, offset);
}
/**
 * Tar inn et JS Date-objekt og returnerer et av TimeValue-objektene fra @internationalized/date-pakken
 * @param {Date | null} date JS Date-objekt som ønskes konvertert til et TimeValue-objekt
 * @param {boolean} noDateOnlyTime Hvis datoen er irrelevant kan denne settes til true, da får man et Time-objekt uten dato tilbake
 * @param {string} timeZone Tidssonen på IANA-formatet som tidpunktet skal konverteres til. Utelates denne får man et tidspunkt uten tidssone. Kan brukes med og uten en UTC-offset Vær obs på annen oppførsel med offset, les mer på beskrivelsen av offset
 * @param {number} offset UTC-offset i millisekunder, må brukes med en tidssone. Ved å legge på en offset lager du en variant av en tidssone. Det betyr at tidspunktet ikke endres (time, minutt, sekund uendret), men tidssonen, med tilhørende offset, tidspunktet er i endres.
 * @returns {Time | CalendarDateTime | ZonedDateTime | null} et av TimeValue-objektene med verdier fra date eller null
 */
function nativeDateToTimeValue(date, noDateOnlyTime, timeZone, offset) {
  if (noDateOnlyTime === void 0) {
    noDateOnlyTime = false;
  }
  if (date === null) return null;
  if (noDateOnlyTime) return new Time(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
  return nativeDateToDateTime(date, timeZone, offset);
}
/**
 * Tar inn et av Date- eller TimeValue-objektene fra \@internationalized/date-pakken og returnerer et JS Date-objekt
 * @param {DateValue | TimeValue | null} value En dato eller et tidspunkt på Date- eller TimeValue-formatet som ønskes konvertert til et JS Date-objekt
 * @param {string} timeZoneForCalendarDateTime Tidssonen value er i. Fungerer kun med typen er CalendarDateTime
 * @returns {Date | null} et Date-objekt med verdier fra time eller null
 */
function timeOrDateValueToNativeDate(value, timeZoneForCalendarDateTime) {
  if (value === null) return null;
  // type is Time
  if (!('day' in value)) {
    var date = new Date();
    date.setHours(value.hour);
    date.setMinutes(value.minute);
    date.setSeconds(value.second);
    date.setMilliseconds(value.millisecond);
    return date;
  }
  // type is CalendarDate
  if (!('hour' in value)) {
    return value.toDate(timeZoneForCalendarDateTime != null ? timeZoneForCalendarDateTime : getLocalTimeZone());
  }
  // type is CalendarDateTime
  if (!('timeZone' in value)) {
    if (timeZoneForCalendarDateTime) return value.toDate(timeZoneForCalendarDateTime);
    return value.toDate(getLocalTimeZone());
  }
  // type is ZonedDateTime
  return value.toDate();
}
var createCalendar = function createCalendar(identifier) {
  if (identifier === void 0) {
    identifier = 'gregory';
  }
  switch (identifier) {
    case 'gregory':
      return new GregorianCalendar();
    default:
      throw new Error("Unsupported calendar " + identifier);
  }
};
var ariaLabelIfNorwegian = function ariaLabelIfNorwegian(norwegianAriaLabel, locale, propsCollection) {
  if (locale.toLowerCase() !== 'no-no') return propsCollection['aria-label'];
  return norwegianAriaLabel;
};
var lastMillisecondOfDay = function lastMillisecondOfDay(dateValue) {
  return toCalendarDateTime(dateValue.add({
    days: 1
  })).add({
    milliseconds: -1
  });
};
var convertValueToType = function convertValueToType(_ref) {
  var value = _ref.value,
    type = _ref.type,
    _ref$timezone = _ref.timezone,
    timezone = _ref$timezone === void 0 ? 'Europe/Oslo' : _ref$timezone;
  if (value === null) return null;
  switch (type) {
    case 'CalendarDate':
      if (!('day' in value)) return today(timezone);
      return toCalendarDate(value);
    case 'CalendarDateTime':
      if (!('day' in value)) return toCalendarDateTime(today(timezone), value);
      return toCalendarDateTime(value);
    case 'ZonedDateTime':
      if (!('day' in value)) return toZoned(toCalendarDateTime(today(timezone), value), timezone);
      return toZoned(value, timezone);
    case 'Time':
      if (!('hour' in value)) return toTime(now(timezone));
      if (!('day' in value)) return value;
      return toTime(value);
    default:
      return value;
  }
};
var modulo = function modulo(a, b) {
  return (a % b + b) % b;
};
var focusSegment = function focusSegment(ref, segment) {
  if (ref.current) {
    var segments = ref.current.querySelectorAll('.eds-date-and-time-field__segment');
    var firstSegment = segments[0];
    var lastSegment = segments[segments.length - 1];
    switch (segment) {
      case 'first':
        return firstSegment.focus();
      case 'last':
        return lastSegment.focus();
    }
  }
};
/** Based on code from https://stackoverflow.com/questions/6117814/get-week-of-year-in-javascript-like-in-php */
function getWeekNumberForDate(date) {
  if (date === null) return -1;
  var calendarDate = convertValueToType({
    value: date,
    type: 'CalendarDate'
  });
  var firstDayOfWeek = startOfWeek(calendarDate, 'no-NO');
  var thursdayOfWeek = firstDayOfWeek.add({
    days: 3
  });
  var firstDayOfYearForThursday = startOfYear(thursdayOfWeek);
  var weekNumber = Math.ceil((thursdayOfWeek.compare(firstDayOfYearForThursday) + 1) / 7);
  return weekNumber;
}
function handleOnChange(_ref2) {
  var value = _ref2.value,
    selectedDate = _ref2.selectedDate,
    forcedReturnType = _ref2.forcedReturnType,
    onChange = _ref2.onChange;
  if (forcedReturnType !== undefined || !selectedDate) {
    return onChange == null ? void 0 : onChange(convertValueToType({
      value: value,
      type: forcedReturnType != null ? forcedReturnType : 'ZonedDateTime',
      timezone: value !== null && 'timezone' in value ? value.timezone : undefined
    }));
  }
  onChange == null || onChange(value);
}

var _excluded$a = ["selectedDate", "onChange", "label", "locale", "showTimeZone", "showTime", "granularity", "disabled", "readOnly", "isDisabled", "variant", "feedback", "validationVariant", "validationFeedback", "labelTooltip", "minDate", "maxDate", "forcedReturnType", "style", "className", "labelProps", "append", "prepend", "dateFieldRef"];
var DateField = function DateField(_ref) {
  var selectedDate = _ref.selectedDate,
    _onChange = _ref.onChange,
    label = _ref.label,
    customLocale = _ref.locale,
    showTimeZone = _ref.showTimeZone,
    showTime = _ref.showTime,
    _ref$granularity = _ref.granularity,
    granularity = _ref$granularity === void 0 ? showTime ? 'minute' : 'day' : _ref$granularity,
    disabled = _ref.disabled,
    readOnly = _ref.readOnly,
    isDisabled = _ref.isDisabled,
    variant = _ref.variant,
    feedback = _ref.feedback,
    _ref$validationVarian = _ref.validationVariant,
    validationVariant = _ref$validationVarian === void 0 ? 'negative' : _ref$validationVarian,
    _ref$validationFeedba = _ref.validationFeedback,
    validationFeedback = _ref$validationFeedba === void 0 ? 'Ugyldig dato' : _ref$validationFeedba,
    labelTooltip = _ref.labelTooltip,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    forcedReturnType = _ref.forcedReturnType,
    style = _ref.style,
    className = _ref.className,
    parentLabelProps = _ref.labelProps,
    append = _ref.append,
    prepend = _ref.prepend,
    ref = _ref.dateFieldRef,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$a);
  var _useLocale = useLocale(),
    locale = _useLocale.locale;
  var _props = _extends({}, rest, {
    label: label,
    locale: customLocale != null ? customLocale : locale,
    createCalendar: createCalendar,
    value: selectedDate,
    onChange: function onChange(value) {
      return handleOnChange({
        value: value,
        selectedDate: selectedDate,
        forcedReturnType: forcedReturnType,
        onChange: _onChange
      });
    },
    hideTimeZone: !showTimeZone,
    granularity: granularity,
    minValue: minDate,
    // this weird logic makes sure the entire day is included if no time is provided in maxDate
    maxValue: 'hour' in (maxDate != null ? maxDate : {}) ? maxDate : maxDate !== undefined ? lastMillisecondOfDay(maxDate) : undefined,
    isDisabled: isDisabled || disabled || readOnly,
    shouldForceLeadingZeros: true
  });
  var state = useDateFieldState(_props);
  var dateFieldRef = useRef(null);
  var _useDateField = useDateField(_props, state, dateFieldRef),
    labelProps = _useDateField.labelProps,
    fieldProps = _useDateField.fieldProps;
  var id = useRandomId('datefield');
  return React.createElement(ConditionalWrapper, {
    condition: customLocale !== undefined,
    wrapper: function wrapper(child) {
      return React.createElement(I18nProvider, {
        locale: customLocale
      }, child);
    }
  }, React.createElement(BaseFormControl, _extends({
    append: append,
    ariaAlertOnFeedback: true,
    className: classNames('eds-datefield', className, {
      'eds-datefield--has-tooltip': labelTooltip !== undefined
    }),
    disabled: isDisabled || disabled,
    readOnly: readOnly,
    disableLabelAnimation: true,
    feedback: feedback != null ? feedback : state.validationState === 'invalid' ? validationFeedback : undefined,
    label: label,
    labelId: id,
    labelProps: parentLabelProps != null ? parentLabelProps : labelProps,
    labelTooltip: labelTooltip,
    prepend: prepend,
    ref: mergeRefs(ref, dateFieldRef),
    style: style,
    variant: variant != null ? variant : state.isInvalid ? validationVariant : undefined
  }, fieldProps), state.segments.map(function (segment, i) {
    return React.createElement(FieldSegment, {
      segment: segment,
      state: state,
      key: i
    });
  })));
};

var _excluded$9 = ["children", "className", "style"];
var CalendarButton = function CalendarButton(_ref) {
  var children = _ref.children,
    className = _ref.className,
    style = _ref.style,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$9);
  var ref = useRef(null);
  var _useButton = useButton(props, ref),
    buttonProps = _useButton.buttonProps;
  return React.createElement(IconButton, _extends({}, buttonProps, {
    ref: ref,
    className: className,
    style: style
  }), children);
};

var _excluded$8 = ["state", "date", "onSelectedCellClick", "weekNumberString", "classNameForDate", "ariaLabelForDate"];
var CalendarCell = function CalendarCell(_ref) {
  var _ariaLabelForDate, _classNameForDate, _state$timeZone, _classNames;
  var state = _ref.state,
    date = _ref.date,
    _ref$onSelectedCellCl = _ref.onSelectedCellClick,
    onSelectedCellClick = _ref$onSelectedCellCl === void 0 ? function () {
      return;
    } : _ref$onSelectedCellCl,
    weekNumberString = _ref.weekNumberString,
    classNameForDate = _ref.classNameForDate,
    ariaLabelForDate = _ref.ariaLabelForDate,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$8);
  var cellRef = useRef(null);
  var _useCalendarCell = useCalendarCell({
      date: date
    }, state, cellRef),
    cellProps = _useCalendarCell.cellProps,
    buttonProps = _useCalendarCell.buttonProps,
    isSelected = _useCalendarCell.isSelected,
    isOutsideVisibleRange = _useCalendarCell.isOutsideVisibleRange,
    isDisabled = _useCalendarCell.isDisabled,
    isUnavailable = _useCalendarCell.isUnavailable,
    formattedDate = _useCalendarCell.formattedDate;
  var ariaLabel = "" + buttonProps['aria-label'] + weekNumberString + " " + ((_ariaLabelForDate = ariaLabelForDate == null ? void 0 : ariaLabelForDate(date)) != null ? _ariaLabelForDate : '');
  return React.createElement("td", _extends({}, cellProps, {
    className: "eds-datepicker__calendar__grid__cell__td"
  }), React.createElement("div", _extends({}, buttonProps, {
    "aria-label": ariaLabel,
    "aria-hidden": isOutsideVisibleRange,
    ref: cellRef,
    hidden: isOutsideVisibleRange,
    className: classNames('eds-datepicker__calendar__grid__cell', (_classNames = {}, _classNames[(_classNameForDate = classNameForDate == null ? void 0 : classNameForDate(date)) != null ? _classNameForDate : ''] = !isOutsideVisibleRange, _classNames['eds-datepicker__calendar__grid__cell--selected'] = isSelected, _classNames['eds-datepicker__calendar__grid__cell--disabled'] = isDisabled || isUnavailable, _classNames['eds-datepicker__calendar__grid__cell--outside-month'] = isOutsideVisibleRange, _classNames['eds-datepicker__calendar__grid__cell--today'] = isEqualDay(date, now((_state$timeZone = state.timeZone) != null ? _state$timeZone : getLocalTimeZone())), _classNames))
  }, rest, {
    onClick: function onClick(e) {
      buttonProps.onClick && buttonProps.onClick(e);
      isSelected && onSelectedCellClick();
    },
    onKeyDown: function onKeyDown(e) {
      buttonProps.onKeyDown && buttonProps.onKeyDown(e);
      if (e.key === 'Enter' || e.key === ' ') isSelected && onSelectedCellClick();
    }
  }), formattedDate));
};

var _excluded$7 = ["state", "navigationDescription", "onSelectedCellClick", "showWeekNumbers", "weekNumberHeader", "classNameForDate", "ariaLabelForDate"];
var CalendarGrid = function CalendarGrid(_ref) {
  var state = _ref.state,
    navigationDescription = _ref.navigationDescription,
    _ref$onSelectedCellCl = _ref.onSelectedCellClick,
    onSelectedCellClick = _ref$onSelectedCellCl === void 0 ? function () {
      return;
    } : _ref$onSelectedCellCl,
    showWeekNumbers = _ref.showWeekNumbers,
    weekNumberHeader = _ref.weekNumberHeader,
    classNameForDate = _ref.classNameForDate,
    ariaLabelForDate = _ref.ariaLabelForDate,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$7);
  var calendarGridId = useRandomId('eds-calendar');
  var _useLocale = useLocale(),
    locale = _useLocale.locale;
  var _useCalendarGrid = useCalendarGrid(rest, state),
    gridProps = _useCalendarGrid.gridProps,
    headerProps = _useCalendarGrid.headerProps,
    weekDays = _useCalendarGrid.weekDays;
  var weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);
  var weeksArray = Array.from(Array(weeksInMonth).keys());
  var weekDaysMapped = function weekDaysMapped() {
    if (locale.toLowerCase().includes('no')) return ['ma', 'ti', 'on', 'to', 'fr', 'lø', 'sø'];
    if (locale.toLowerCase().includes('en')) {
      if (weekDays[0] === 'M') return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
      if (weekDays[0] === 'S') return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    }
    return weekDays.map(function (day) {
      return day.toLowerCase();
    });
  };
  var getNavigationDescription = function getNavigationDescription() {
    if (navigationDescription) return navigationDescription;
    if (locale.toLowerCase().includes('en')) return 'Use the arrow keys to navigate between dates';
    return 'Bruk piltastene til å navigere mellom datoer';
  };
  return React.createElement(React.Fragment, null, React.createElement("table", _extends({}, gridProps, {
    cellSpacing: "0",
    className: "eds-datepicker__calendar__grid"
  }), React.createElement("thead", _extends({}, headerProps), React.createElement("tr", null, showWeekNumbers && React.createElement("th", {
    className: "eds-datepicker__calendar__grid__weeknumber-header"
  }, weekNumberHeader), weekDaysMapped().map(function (day) {
    return React.createElement("th", {
      key: day
    }, day);
  }))), React.createElement("tbody", null, weeksArray.map(function (weekIndex) {
    var weekNumber = getWeekNumberForDate(state.getDatesInWeek(weekIndex)[0]);
    return React.createElement("tr", {
      key: weekIndex
    }, showWeekNumbers && React.createElement("th", {
      "aria-hidden": true,
      className: "eds-datepicker__calendar__grid__weeknumber"
    }, weekNumber), state.getDatesInWeek(weekIndex).map(function (date, i) {
      return date ? React.createElement(CalendarCell, {
        key: date.month + "." + date.day,
        state: state,
        date: date,
        "aria-describedby": calendarGridId + 'description',
        weekNumberString: showWeekNumbers ? ", " + weekNumberHeader + " " + weekNumber + "," : '',
        onSelectedCellClick: onSelectedCellClick,
        classNameForDate: classNameForDate,
        ariaLabelForDate: ariaLabelForDate
      }) : React.createElement("td", {
        key: i
      });
    }));
  }))), React.createElement(VisuallyHidden, {
    id: calendarGridId + 'description'
  }, getNavigationDescription()));
};

var _excluded$6 = ["locale"],
  _excluded2$3 = ["selectedDate", "onChange", "minDate", "maxDate", "showWeekNumbers", "weekNumberHeader", "forcedReturnType", "style", "className", "navigationDescription", "onSelectedCellClick", "classNameForDate", "ariaLabelForDate", "calendarRef"];
var Calendar = function Calendar(_ref) {
  var localOverride = _ref.locale,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$6);
  var _useLocale = useLocale(),
    locale = _useLocale.locale;
  return React.createElement(I18nProvider, {
    locale: localOverride != null ? localOverride : locale
  }, React.createElement(CalendarBase, _extends({}, rest)));
};
var CalendarBase = function CalendarBase(_ref2) {
  var selectedDate = _ref2.selectedDate,
    _onChange = _ref2.onChange,
    minDate = _ref2.minDate,
    maxDate = _ref2.maxDate,
    _ref2$showWeekNumbers = _ref2.showWeekNumbers,
    showWeekNumbers = _ref2$showWeekNumbers === void 0 ? false : _ref2$showWeekNumbers,
    _ref2$weekNumberHeade = _ref2.weekNumberHeader,
    weekNumberHeader = _ref2$weekNumberHeade === void 0 ? 'uke' : _ref2$weekNumberHeade,
    forcedReturnType = _ref2.forcedReturnType,
    style = _ref2.style,
    className = _ref2.className,
    navigationDescription = _ref2.navigationDescription,
    _ref2$onSelectedCellC = _ref2.onSelectedCellClick,
    onSelectedCellClick = _ref2$onSelectedCellC === void 0 ? function () {
      return;
    } : _ref2$onSelectedCellC,
    classNameForDate = _ref2.classNameForDate,
    ariaLabelForDate = _ref2.ariaLabelForDate,
    calendarRef = _ref2.calendarRef,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2$3);
  var _useLocale2 = useLocale(),
    locale = _useLocale2.locale;
  var _props = _extends({}, rest, {
    value: selectedDate,
    onChange: function onChange(value) {
      return handleOnChange({
        value: value,
        selectedDate: selectedDate,
        forcedReturnType: forcedReturnType,
        onChange: _onChange
      });
    },
    locale: locale,
    createCalendar: createCalendar,
    minValue: minDate,
    maxValue: maxDate
  });
  var state = useCalendarState(_props);
  var _useCalendar = useCalendar(_props, state),
    calendarProps = _useCalendar.calendarProps,
    prevButtonProps = _useCalendar.prevButtonProps,
    nextButtonProps = _useCalendar.nextButtonProps,
    title = _useCalendar.title;
  return React.createElement("div", _extends({}, calendarProps, {
    ref: calendarRef,
    className: classNames('eds-datepicker__calendar', className),
    style: style
  }), React.createElement("div", {
    className: "eds-datepicker__calendar__header"
  }, React.createElement(CalendarButton, _extends({}, prevButtonProps, {
    "aria-label": ariaLabelIfNorwegian('Forrige måned', locale, prevButtonProps)
  }), React.createElement(LeftArrowIcon, {
    size: 20
  })), React.createElement("h2", null, title), React.createElement(CalendarButton, _extends({}, nextButtonProps, {
    "aria-label": ariaLabelIfNorwegian('Neste måned', locale, nextButtonProps)
  }), React.createElement(RightArrowIcon, {
    size: 20
  }))), React.createElement(CalendarGrid, _extends({}, rest, {
    state: state,
    navigationDescription: navigationDescription,
    onSelectedCellClick: onSelectedCellClick,
    classNameForDate: classNameForDate,
    ariaLabelForDate: ariaLabelForDate,
    showWeekNumbers: showWeekNumbers,
    weekNumberHeader: weekNumberHeader
  })));
};

var _excluded$5 = ["selectedDate", "locale", "disabled", "readOnly", "showTime", "showTimeZone", "classNameForDate", "className", "variant", "feedback", "validationVariant", "validationFeedback", "showWeekNumbers", "weekNumberHeader", "disableModal", "labelTooltip", "navigationDescription", "minDate", "maxDate", "modalTreshold", "forcedReturnType", "ariaLabelForDate", "append", "prepend", "granularity"];
var DatePicker = function DatePicker(_ref) {
  var selectedDate = _ref.selectedDate,
    locale = _ref.locale,
    disabled = _ref.disabled,
    readOnly = _ref.readOnly,
    showTime = _ref.showTime,
    _ref$showTimeZone = _ref.showTimeZone,
    showTimeZone = _ref$showTimeZone === void 0 ? false : _ref$showTimeZone,
    classNameForDate = _ref.classNameForDate,
    className = _ref.className,
    variant = _ref.variant,
    feedback = _ref.feedback,
    validationVariant = _ref.validationVariant,
    validationFeedback = _ref.validationFeedback,
    showWeekNumbers = _ref.showWeekNumbers,
    weekNumberHeader = _ref.weekNumberHeader,
    _ref$disableModal = _ref.disableModal,
    disableModal = _ref$disableModal === void 0 ? false : _ref$disableModal,
    labelTooltip = _ref.labelTooltip,
    navigationDescription = _ref.navigationDescription,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    _ref$modalTreshold = _ref.modalTreshold,
    modalTreshold = _ref$modalTreshold === void 0 ? 1000 : _ref$modalTreshold,
    ariaLabelForDate = _ref.ariaLabelForDate,
    append = _ref.append,
    prepend = _ref.prepend,
    _ref$granularity = _ref.granularity,
    granularity = _ref$granularity === void 0 ? showTime ? 'minute' : 'day' : _ref$granularity,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$5);
  var CALENDAR_MODAL_MAX_SCREEN_WIDTH = modalTreshold;
  var datePickerRef = useRef(null);
  var calendarRef = useRef(null);
  var _useWindowDimensions = useWindowDimensions(),
    width = _useWindowDimensions.width;
  var _props = _extends({}, rest, {
    minValue: minDate,
    // this weird logic makes sure the entire day is included if no time is provided in maxDate
    maxValue: 'hour' in (maxDate != null ? maxDate : {}) ? maxDate : maxDate !== undefined ? lastMillisecondOfDay(maxDate) : undefined,
    value: selectedDate,
    granularity: granularity,
    isDisabled: disabled || readOnly
  });
  var state = useDatePickerState(_props);
  var _useDatePicker = useDatePicker(_props, state, datePickerRef),
    groupProps = _useDatePicker.groupProps,
    labelProps = _useDatePicker.labelProps,
    fieldProps = _useDatePicker.fieldProps,
    buttonProps = _useDatePicker.buttonProps,
    dialogProps = _useDatePicker.dialogProps,
    calendarProps = _useDatePicker.calendarProps;
  // calculations for floating-UI popover position
  var _useFloating = useFloating({
      whileElementsMounted: function whileElementsMounted(ref, _float, update) {
        return autoUpdate(ref, _float, update, {
          elementResize: false
        });
      },
      placement: 'bottom-start',
      middleware: [offset(space.extraSmall2), flip(), shift({
        padding: space.extraSmall
      })]
    }),
    refs = _useFloating.refs,
    floatingStyles = _useFloating.floatingStyles,
    update = _useFloating.update;
  useOnClickOutside([calendarRef], function () {
    state.setOpen(false);
  });
  useOnEscape(calendarRef, function () {
    state.setOpen(false);
  });
  var calendarSharedProps = _extends({}, dialogProps, calendarProps, {
    onChange: rest.onChange,
    disabled: disabled,
    navigationDescription: navigationDescription,
    onSelectedCellClick: function onSelectedCellClick() {
      return state.setOpen(false);
    },
    selectedDate: selectedDate,
    minDate: minDate,
    maxDate: maxDate,
    calendarRef: calendarRef,
    classNameForDate: classNameForDate,
    ariaLabelForDate: ariaLabelForDate,
    showWeekNumbers: showWeekNumbers,
    weekNumberHeader: weekNumberHeader
  });
  var isModal = typeof width !== 'undefined' && width <= CALENDAR_MODAL_MAX_SCREEN_WIDTH && !disableModal;
  var popoverCalendar = React.createElement("div", {
    style: _extends({}, floatingStyles, {
      zIndex: zIndexes.popover
    }),
    ref: refs.setFloating
  }, React.createElement(FocusLock, {
    disabled: !state.isOpen || isModal,
    returnFocus: true
  }, state.isOpen && React.createElement(Calendar, _extends({}, calendarSharedProps))));
  var modalCalendar = React.createElement(Modal, {
    size: "small",
    title: "",
    open: state.isOpen,
    onDismiss: function onDismiss() {
      return state.setOpen(false);
    },
    className: "eds-datepicker__calendar-modal"
  }, React.createElement(Calendar, _extends({}, calendarSharedProps)));
  return React.createElement(ConditionalWrapper, {
    condition: locale !== undefined,
    wrapper: function wrapper(child) {
      return React.createElement(I18nProvider, {
        locale: locale
      }, child);
    }
  }, React.createElement(DateField, _extends({}, groupProps, fieldProps, rest, {
    append: !disabled && !readOnly && React.createElement("div", {
      style: {
        display: 'flex',
        alignItems: 'center'
      }
    }, append, React.createElement(CalendarButton, _extends({}, buttonProps, {
      onPress: function onPress() {
        state.setOpen(!state.isOpen);
        update();
      },
      className: "eds-datepicker__open-calendar-button"
    }), React.createElement(CalendarIcon, null))),
    prepend: prepend,
    className: classNames('eds-datepicker', className, {
      'eds-datepicker--disabled': disabled
    }),
    disabled: disabled,
    readOnly: readOnly,
    feedback: feedback,
    labelProps: labelProps,
    labelTooltip: labelTooltip,
    maxDate: maxDate,
    minDate: minDate,
    dateFieldRef: function dateFieldRef(node) {
      refs.setReference(node);
      datePickerRef.current = node;
    },
    selectedDate: selectedDate,
    showTime: showTime,
    showTimeZone: showTimeZone,
    validationFeedback: validationFeedback,
    validationVariant: validationVariant,
    variant: variant
  })), isModal ? modalCalendar : popoverCalendar);
};

var _excluded$4 = ["className", "style", "label", "onChange", "feedback", "variant", "disableLabelAnimation", "prepend"],
  _excluded2$2 = ["onChange", "variant", "value"];
var NativeDatePicker = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    style = _ref.style,
    label = _ref.label,
    onChange = _ref.onChange,
    feedback = _ref.feedback,
    variant = _ref.variant,
    disableLabelAnimation = _ref.disableLabelAnimation,
    _ref$prepend = _ref.prepend,
    prepend = _ref$prepend === void 0 ? React.createElement(DateIcon, {
      inline: true
    }) : _ref$prepend,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$4);
  var nativedatepickerId = useRandomId('eds-nativetimepicker');
  return React.createElement(BaseFormControl, {
    style: style,
    className: className,
    prepend: prepend,
    label: label,
    feedback: feedback,
    variant: variant,
    labelId: nativedatepickerId,
    disableLabelAnimation: disableLabelAnimation,
    isFilled: true
  }, React.createElement(NativeDatePickerBase, _extends({
    onChange: onChange,
    "aria-labelledby": nativedatepickerId,
    ref: ref,
    variant: variant
  }, rest)));
});
var NativeDatePickerBase = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var onChange = _ref2.onChange,
    variant = _ref2.variant,
    value = _ref2.value,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2$2);
  var contextVariant = useVariant();
  var currentVariant = variant || contextVariant;
  var _useInputGroupContext = useInputGroupContext(),
    isDatepickerFilled = _useInputGroupContext.isFilled,
    setFiller = _useInputGroupContext.setFilled;
  useOnMount(function () {
    setFiller && !isDatepickerFilled && setFiller(true);
  });
  React.useEffect(function () {
    if (value) {
      setFiller && !isDatepickerFilled && setFiller(true);
    } else {
      setFiller && isDatepickerFilled && setFiller(false);
    }
  }, [value, setFiller, isDatepickerFilled]);
  var handleChange = function handleChange(event) {
    if (isFilled(event.target)) {
      setFiller && !isDatepickerFilled && setFiller(true);
    } else {
      setFiller && isDatepickerFilled && setFiller(false);
    }
    if (onChange) {
      onChange(event);
    }
  };
  return React.createElement("input", _extends({
    ref: ref,
    "aria-invalid": currentVariant === 'error',
    type: "date",
    className: "eds-form-control eds-native-date-picker",
    onChange: handleChange,
    value: value
  }, rest));
});

var _excluded$3 = ["direction", "onClick", "disabled", "readonly", "aria-label"];
var TimePickerArrowButton = function TimePickerArrowButton(_ref) {
  var direction = _ref.direction,
    onClick = _ref.onClick,
    disabled = _ref.disabled,
    ariaLabel = _ref['aria-label'],
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$3);
  return React.createElement(IconButton, _extends({
    className: classNames('eds-timepicker__arrowbutton', "eds-timepicker__arrowbutton--" + direction, {
      'eds-timepicker__arrowbutton--disabled': disabled
    }),
    type: "button",
    tabIndex: -1,
    onClick: onClick,
    "aria-label": ariaLabel,
    disabled: disabled
  }, rest), direction === 'left' ? React.createElement(LeftArrowIcon, null) : React.createElement(RightArrowIcon, null));
};

var _excluded$2 = ["selectedTime", "onChange", "disabled", "readOnly", "className", "style", "label", "labelTooltip", "feedback", "granularity", "variant", "locale", "showTimeZone", "showSeconds", "minuteIncrementForArrowButtons", "leftArrowButtonAriaLabel", "rightArrowButtonAriaLabel", "inputRef", "forcedReturnType", "forcedTimeZone", "append", "prepend"];
var TimePicker = function TimePicker(_ref) {
  var selectedTime = _ref.selectedTime,
    onChange = _ref.onChange,
    disabled = _ref.disabled,
    readOnly = _ref.readOnly,
    className = _ref.className,
    style = _ref.style,
    label = _ref.label,
    labelTooltip = _ref.labelTooltip,
    feedback = _ref.feedback,
    granularity = _ref.granularity,
    variant = _ref.variant,
    customLocale = _ref.locale,
    showTimeZone = _ref.showTimeZone,
    _ref$showSeconds = _ref.showSeconds,
    showSeconds = _ref$showSeconds === void 0 ? false : _ref$showSeconds,
    _ref$minuteIncrementF = _ref.minuteIncrementForArrowButtons,
    minuteIncrementForArrowButtons = _ref$minuteIncrementF === void 0 ? 30 : _ref$minuteIncrementF,
    _ref$leftArrowButtonA = _ref.leftArrowButtonAriaLabel,
    leftArrowButtonAriaLabel = _ref$leftArrowButtonA === void 0 ? "Trekk fra " + minuteIncrementForArrowButtons + " minutter" : _ref$leftArrowButtonA,
    _ref$rightArrowButton = _ref.rightArrowButtonAriaLabel,
    rightArrowButtonAriaLabel = _ref$rightArrowButton === void 0 ? "Legg til " + minuteIncrementForArrowButtons + " minutter" : _ref$rightArrowButton,
    inputRef = _ref.inputRef,
    forcedReturnType = _ref.forcedReturnType,
    forcedTimeZone = _ref.forcedTimeZone,
    append = _ref.append,
    prepend = _ref.prepend,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  var _useLocale = useLocale(),
    locale = _useLocale.locale;
  if (customLocale) locale = customLocale;
  var timePickerId = useRandomId('eds-timepicker');
  var timeZone = forcedTimeZone != null ? forcedTimeZone : selectedTime !== null && 'timezone' in selectedTime ? selectedTime.timezone : 'Europe/Oslo';
  var handleOnChange = function handleOnChange(value) {
    if (forcedReturnType !== undefined || !selectedTime) {
      return onChange(convertValueToType({
        value: value,
        type: forcedReturnType != null ? forcedReturnType : 'ZonedDateTime',
        timezone: timeZone
      }));
    }
    onChange(value);
  };
  var state = useTimeFieldState(_extends({
    onChange: handleOnChange,
    label: label,
    locale: locale,
    value: selectedTime,
    granularity: (granularity != null ? granularity : showSeconds) ? 'second' : 'minute',
    hideTimeZone: !showTimeZone,
    isDisabled: disabled || readOnly,
    shouldForceLeadingZeros: true
  }, rest));
  var timeFieldRef = useRef(null);
  var _useTimeField = useTimeField(_extends({}, rest, {
      label: label
    }), state, timeFieldRef),
    labelProps = _useTimeField.labelProps,
    fieldProps = _useTimeField.fieldProps;
  var id = useRandomId('timepicker');
  var getCurrentTime = function getCurrentTime() {
    var getCurrentTimeWithCorrectType = convertValueToType({
      value: now(timeZone),
      type: forcedReturnType != null ? forcedReturnType : 'ZonedDateTime'
    });
    return getCurrentTimeWithCorrectType;
  };
  var handleOnClickArrowButton = function handleOnClickArrowButton(operation) {
    if (selectedTime === null) return handleOnChange(getCurrentTime());
    switch (operation) {
      case 'add':
        return handleOnChange(selectedTime.add({
          minutes: minuteIncrementForArrowButtons - modulo(selectedTime.minute, minuteIncrementForArrowButtons)
        }));
      case 'subtract':
        return handleOnChange(selectedTime.subtract({
          minutes: modulo(selectedTime.minute - 1, minuteIncrementForArrowButtons) + 1
        }));
    }
  };
  return React.createElement(I18nProvider, {
    locale: locale
  }, React.createElement(BaseFormControl, {
    append: React.createElement("div", {
      style: {
        display: 'flex',
        alignItems: 'center'
      }
    }, append, React.createElement(TimePickerArrowButton, {
      direction: "right",
      disabled: disabled || readOnly,
      "aria-label": rightArrowButtonAriaLabel,
      onClick: function onClick() {
        return handleOnClickArrowButton('add');
      },
      onFocus: function onFocus() {
        return focusSegment(timeFieldRef, 'last');
      }
    })),
    ariaAlertOnFeedback: true,
    "aria-describedby": timePickerId + 'description',
    className: classNames('eds-timepicker', className, {
      'eds-timepicker--disabled': disabled,
      'eds-timepicker--has-tooltip': labelTooltip !== undefined,
      'eds-timepicker--readonly': readOnly
    }),
    disabled: disabled,
    readOnly: readOnly,
    disableLabelAnimation: true,
    feedback: feedback,
    label: label,
    labelId: id,
    labelProps: _extends({}, labelProps, {
      'aria-describedby': timePickerId + 'description'
    }),
    labelTooltip: labelTooltip,
    prepend: React.createElement("div", {
      style: {
        display: 'flex',
        alignItems: 'center'
      }
    }, React.createElement(TimePickerArrowButton, {
      direction: "left",
      disabled: disabled || readOnly,
      "aria-label": leftArrowButtonAriaLabel,
      onClick: function onClick() {
        return handleOnClickArrowButton('subtract');
      },
      onFocus: function onFocus() {
        return focusSegment(timeFieldRef, 'first');
      }
    }), prepend),
    ref: inputRef,
    style: style,
    variant: variant
  }, React.createElement("span", _extends({
    ref: timeFieldRef
  }, fieldProps, {
    style: {
      display: 'contents'
    }
  }), state.segments.map(function (segment, i) {
    return React.createElement(FieldSegment, {
      segment: segment,
      state: state,
      key: i,
      "aria-describedby": timePickerId + 'description'
    });
  })), React.createElement(VisuallyHidden, {
    id: timePickerId + 'description'
  }, selectedTime !== null ? 'valgt tid: ' + selectedTime.hour.toString().padStart(2, '0') + ':' + selectedTime.minute.toString().padStart(2, '0') + (showSeconds ? ':' + selectedTime.second.toString().padStart(2, '0') : '') : '')));
};

var _excluded$1 = ["className", "style", "onChange", "label", "feedback", "variant", "prepend"],
  _excluded2$1 = ["onChange", "value"];
var NativeTimePicker = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    style = _ref.style,
    onChange = _ref.onChange,
    label = _ref.label,
    feedback = _ref.feedback,
    variant = _ref.variant,
    prepend = _ref.prepend,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  var nativetimepickerId = useRandomId('eds-native-timepicker');
  return React.createElement(BaseFormControl, {
    style: style,
    className: classNames(className, 'eds-native-timepicker'),
    prepend: prepend,
    label: label,
    feedback: feedback,
    variant: variant,
    labelId: nativetimepickerId,
    disableLabelAnimation: true
  }, React.createElement(NativeTimePickerBase, _extends({
    onChange: onChange,
    "aria-labelledby": nativetimepickerId,
    ref: ref
  }, rest)));
});
var NativeTimePickerBase = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var onChange = _ref2.onChange,
    value = _ref2.value,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2$1);
  var contextVariant = useVariant();
  var currentVariant = rest.variant || contextVariant;
  var _useInputGroupContext = useInputGroupContext(),
    isTimepickerFilled = _useInputGroupContext.isFilled,
    setFiller = _useInputGroupContext.setFilled;
  useOnMount(function () {
    setFiller && !isTimepickerFilled && setFiller(true);
  });
  React.useEffect(function () {
    if (value) {
      setFiller && !isTimepickerFilled && setFiller(true);
    } else {
      setFiller && isTimepickerFilled && setFiller(false);
    }
  }, [value, setFiller, isTimepickerFilled]);
  var handleChange = function handleChange(event) {
    if (isFilled(event.target)) {
      setFiller && !isTimepickerFilled && setFiller(true);
    } else {
      setFiller && isTimepickerFilled && setFiller(false);
    }
    if (onChange) {
      onChange(event);
    }
  };
  return React.createElement("input", _extends({
    ref: ref,
    "aria-invalid": currentVariant === 'error',
    type: "time",
    className: "eds-form-control",
    onChange: handleChange,
    value: value
  }, rest));
});

var _excluded = ["append", "className", "disabled", "feedback", "showClockIcon", "inputRef", "label", "labelTooltip", "onChange", "padding", "prepend", "readOnly", "selectedTime", "showSeconds", "style", "variant"],
  _excluded2 = ["onBlur", "onClick", "onDragStart", "onFocus", "onKeyDown", "onKeyUp", "onMouseDown", "onPointerDown", "onPointerUp"];
var inputResult;
(function (inputResult) {
  inputResult[inputResult["RESET_TIME"] = 0] = "RESET_TIME";
  inputResult[inputResult["INVALID"] = 1] = "INVALID";
})(inputResult || (inputResult = {}));
var SimpleTimePicker = function SimpleTimePicker(_ref) {
  var append = _ref.append,
    disabled = _ref.disabled,
    feedback = _ref.feedback,
    _ref$showClockIcon = _ref.showClockIcon,
    showClockIcon = _ref$showClockIcon === void 0 ? 'right' : _ref$showClockIcon,
    inputRef = _ref.inputRef,
    label = _ref.label,
    labelTooltip = _ref.labelTooltip,
    onChange = _ref.onChange,
    _ref$padding = _ref.padding,
    padding = _ref$padding === void 0 ? 'default' : _ref$padding,
    prepend = _ref.prepend,
    readOnly = _ref.readOnly,
    selectedTime = _ref.selectedTime,
    showSeconds = _ref.showSeconds,
    style = _ref.style,
    variant = _ref.variant,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var _useState = useState(''),
    inputText = _useState[0],
    setInputText = _useState[1];
  var timeFieldRef = useRef(null);
  var _useState2 = useState(null),
    lastValidSelectedTime = _useState2[0],
    setLastValidSelectedTime = _useState2[1];
  var _useLocale = useLocale(),
    locale = _useLocale.locale;
  var state = useTimeFieldState(_extends({
    onChange: onChange,
    label: label,
    locale: locale,
    value: selectedTime,
    hideTimeZone: true,
    isDisabled: disabled,
    isReadOnly: readOnly
  }, rest));
  var _useTimeField = useTimeField(_extends({}, rest, {
      label: label
    }), state, timeFieldRef),
    labelProps = _useTimeField.labelProps,
    fieldProps = _useTimeField.fieldProps;
  useEffect(function () {
    updateInputWithSelectedTime();
    // @ts-expect-error TimeType and MappedTimeValue<TimeType> is actually the same
    if (selectedTime !== null) setLastValidSelectedTime(selectedTime);
  }, [selectedTime == null ? void 0 : selectedTime.toString()]);
  var updateInputWithSelectedTime = function updateInputWithSelectedTime() {
    var selectedTimeString = getStringFromTimeValue(selectedTime);
    setInputText(selectedTimeString);
    var timeFieldIsFocused = document.activeElement === (timeFieldRef == null ? void 0 : timeFieldRef.current);
    if (selectedTimeString === '' && !timeFieldIsFocused) addPlaceholderToInput();
  };
  var getStringFromTimeValue = function getStringFromTimeValue(timeValue) {
    if (timeValue === null) return '';
    var timeObject = 'day' in timeValue ? toTime(timeValue) : timeValue;
    if (showSeconds) return timeObject.toString().slice(0, 8);
    return timeObject.toString().slice(0, 5);
  };
  var addPlaceholderToInput = function addPlaceholderToInput() {
    if (showSeconds) setInputText('–– : –– : ––');else setInputText('–– : ––');
  };
  var handleChangeTime = function handleChangeTime() {
    var newTime = getValueForOnChangeFromInput();
    if (newTime === inputResult.INVALID) {
      return updateInputWithSelectedTime();
    }
    if ((newTime == null ? void 0 : newTime.toString()) !== (selectedTime == null ? void 0 : selectedTime.toString())) onChange == null || onChange(newTime);
  };
  var getValueForOnChangeFromInput = function getValueForOnChangeFromInput() {
    var formatedTimeString = formatTimeString(inputText);
    var newTimeObject = formatedTimeStringToTimeObject(formatedTimeString);
    if (newTimeObject === inputResult.INVALID) {
      return inputResult.INVALID;
    }
    if (newTimeObject === inputResult.RESET_TIME) {
      return null;
    }
    var updatedSelectedTime = getSelectedTimeWithTimeFromObject(newTimeObject);
    return updatedSelectedTime;
  };
  var formatTimeString = function formatTimeString(timeString) {
    if (timeString.length === 0) return inputResult.RESET_TIME;
    if (timeString.length < 3 || timeString.length > 8) return inputResult.INVALID;
    var numberOfColons = (timeString.match(new RegExp(':', 'g')) || []).length;
    var stringLength = timeString.length;
    // targets 'd:dd:dd' and 'dd:dd:dd'
    if (numberOfColons === 2 && stringLength >= 7) {
      return timeString.padStart(8, '0');
    }
    // targets 'd:dd' and 'dd:dd'
    if (numberOfColons === 1) {
      return timeString.padStart(5, '0');
    }
    if (stringLength > 6) return inputResult.INVALID;
    var stringLengthIsEven = stringLength % 2 == 0;
    var hourString = stringLengthIsEven ? timeString.slice(0, 2) : timeString.slice(0, 1);
    var minuteString = stringLengthIsEven ? timeString.slice(2, 4) : timeString.slice(1, 3);
    var secondString = function () {
      var stringSlice = stringLengthIsEven ? timeString.slice(4, 6) : timeString.slice(3, 5);
      if (stringSlice === '') return '00';
      return stringSlice;
    }();
    var timeStringWithColon = hourString.padStart(2, '0') + ':' + minuteString + (showSeconds ? ':' + secondString : '');
    return timeStringWithColon;
  };
  var formatedTimeStringToTimeObject = function formatedTimeStringToTimeObject(formatedTimeString) {
    if (formatedTimeString === inputResult.INVALID) return inputResult.INVALID;
    if (formatedTimeString === inputResult.RESET_TIME) return inputResult.RESET_TIME;
    var isNumberTest = /^\d+$/;
    var hourString = formatedTimeString.slice(0, 2);
    var minuteString = formatedTimeString.slice(3, 5);
    var secondString = formatedTimeString.slice(6, 8);
    if (hourString.match(isNumberTest) && minuteString.match(isNumberTest) && (secondString === '' || secondString.match(isNumberTest))) {
      try {
        var timeObject = parseTime(formatedTimeString);
        return timeObject;
      } catch (e) {
        return inputResult.INVALID;
      }
    }
    return inputResult.INVALID;
  };
  var getSelectedTimeWithTimeFromObject = function getSelectedTimeWithTimeFromObject(newTime) {
    var selectedTimeWithUpdateTime = (lastValidSelectedTime != null ? lastValidSelectedTime : new Time()).set({
      hour: newTime.hour,
      minute: newTime.minute,
      second: newTime.second
    });
    return selectedTimeWithUpdateTime;
  };
  var usedFieldProps = _objectWithoutPropertiesLoose(fieldProps, _excluded2);
  return React.createElement(I18nProvider, {
    locale: locale
  }, React.createElement(TextField, _extends({
    append: append || (showClockIcon === true || showClockIcon === 'right' ? React.createElement(ClockIcon, {
      onClick: function onClick() {
        var _timeFieldRef$current;
        return timeFieldRef == null || (_timeFieldRef$current = timeFieldRef.current) == null ? void 0 : _timeFieldRef$current.focus();
      },
      inline: true
    }) : undefined),
    className: classNames('eds-simple-timepicker', {
      'eds-simple-timepicker--padding-large': padding === 'large',
      'eds-simple-timepicker--show-seconds': showSeconds,
      'eds-simple-timepicker--hide-clock': !showClockIcon,
      'eds-simple-timepicker--has-tooltip': labelTooltip !== undefined
    }),
    disabled: disabled,
    disableLabelAnimation: true,
    feedback: feedback,
    label: label,
    labelProps: labelProps,
    labelTooltip: labelTooltip,
    onBlur: function onBlur() {
      handleChangeTime();
      if (selectedTime === null) {
        addPlaceholderToInput();
      }
    },
    onChange: function onChange(e) {
      return setInputText(e.target.value);
    },
    onFocus: function onFocus() {
      if (selectedTime === null) {
        setInputText('');
      }
    },
    onKeyDown: function onKeyDown(_ref2) {
      var key = _ref2.key;
      if (key === 'Enter') handleChangeTime();
    },
    placeholder: showSeconds ? '–– : –– : ––' : '–– : ––',
    prepend: prepend || (showClockIcon === 'left' ? React.createElement(ClockIcon, {
      onClick: function onClick() {
        var _timeFieldRef$current2;
        return timeFieldRef == null || (_timeFieldRef$current2 = timeFieldRef.current) == null ? void 0 : _timeFieldRef$current2.focus();
      }
    }) : undefined),
    readOnly: readOnly,
    ref: mergeRefs(timeFieldRef, inputRef),
    style: style,
    value: inputText,
    variant: variant
  }, usedFieldProps)));
};

warnAboutMissingStyles('datepicker', 'form', 'icons');

export { Calendar, DateField, DatePicker, NativeDatePicker, NativeTimePicker, SimpleTimePicker, TimePicker, ariaLabelIfNorwegian, convertValueToType, createCalendar, focusSegment, getWeekNumberForDate, handleOnChange, lastMillisecondOfDay, modulo, nativeDateToDateValue, nativeDateToTimeValue, timeOrDateValueToNativeDate };
//# sourceMappingURL=datepicker.esm.js.map
